import React, { createContext, useContext, useState, useCallback, ReactNode, useMemo, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import { agentApi, type AgentConversationSummary } from '../services/reportApi';
import type { ToolWidgetData } from '../types/agentWidgets';

// ─── Types ──────────────────────────────────────────────────────────────────────

export type SidebarMode = 'ask' | 'revise' | 'revise-selection' | 'write';

export type SidebarTab = 'documents' | 'assistant' | 'wizard';

export type PageType = 'case-detail' | 'report-editor' | 'report-viewer' | 'other';

export interface PageContext {
  pageType: PageType;
  caseId: string | null;
  reportId: string | null;
  editor: Editor | null;
  editorSelectedText: string | null;
}

export interface ContextItem {
  id: string;
  type: 'event' | 'gap' | 'inconsistency' | 'standard-of-care' | 'comparative' | 'causation' | 'text' | 'expert-opinion';
  summary: string;
  source: string;
  data: Record<string, unknown>;
}

export interface AgentToolCall {
  toolName: string;
  toolUseId: string;
  input: Record<string, unknown>;
  summary?: string;
  widgetData?: ToolWidgetData & { outputWidget?: Record<string, unknown> };
  status: 'pending' | 'running' | 'done' | 'error';
}

export interface AgentConversation {
  id: string;
  caseId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  sources?: AskSource[];
  isStreaming?: boolean;
  toolCalls?: AgentToolCall[];
  isThinking?: boolean;
  contextMetadata?: {
    totalRequested: number;
    totalLoaded: number;
    failures: Array<{ type: string; id: string; reason: string }>;
  };
}

export interface AskSource {
  documentId: string;
  documentName: string;
  pageNumber?: number;
  endPageNumber?: number;
  excerpt?: string;
  sourceText?: string;
  chunkIndex?: number;
  sourceType?: string;
}

// ─── Tab State ───────────────────────────────────────────────────────────────────

export interface TabState {
  tabId: string;
  conversationId: string | null;
  title: string;
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  lastActiveAt: number;
}

const MAX_TABS = 8;

function createEmptyTab(tabId: string): TabState {
  return {
    tabId,
    conversationId: null,
    title: 'New conversation',
    messages: [],
    isLoading: false,
    isStreaming: false,
    lastActiveAt: Date.now(),
  };
}

/**
 * Parse a message DTO from the backend into a ChatMessage.
 * For assistant messages, the toolCalls column may contain sources JSON
 * (array of {documentName, ...}) rather than tool calls ({toolName, ...}).
 */
function parseDtoToMessage(m: { id: string; role: string; content: string; createdAt: string; toolCalls?: string }): ChatMessage {
  let sources: AskSource[] | undefined;
  let toolCalls: AgentToolCall[] | undefined;

  if (m.toolCalls) {
    try {
      const parsed = JSON.parse(m.toolCalls);
      if (Array.isArray(parsed) && parsed.length > 0) {
        if (m.role === 'assistant' && parsed[0].documentName) {
          sources = parsed.map((s: Record<string, unknown>) => ({
            documentId: (s.documentId as string) || '',
            documentName: (s.documentName as string) || 'Unknown',
            pageNumber: s.pageNumber as number | undefined,
            endPageNumber: s.endPageNumber as number | undefined,
            excerpt: s.excerpt as string | undefined,
            sourceText: s.sourceText as string | undefined,
            chunkIndex: s.chunkIndex as number | undefined,
          }));
        } else {
          toolCalls = parsed;
        }
      }
    } catch { /* ignore parse errors */ }
  }

  return {
    id: m.id,
    role: m.role as 'user' | 'assistant',
    content: m.content,
    timestamp: new Date(m.createdAt),
    toolCalls,
    sources,
  };
}

// ─── Context Type ────────────────────────────────────────────────────────────────

interface AskSidebarContextType {
  // Sidebar visibility
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  // Active sidebar tab (documents / assistant / wizard)
  activeSidebarTab: SidebarTab;
  setActiveSidebarTab: (tab: SidebarTab) => void;

  // Current mode (within the assistant tab)
  mode: SidebarMode;
  setMode: (mode: SidebarMode) => void;
  availableModes: SidebarMode[];

  // Page context (set by pages via hook)
  pageContext: PageContext;
  setPageContext: (ctx: Partial<PageContext>) => void;

  // Context basket (events, findings, text selections)
  contextItems: ContextItem[];
  addToContext: (item: ContextItem) => void;
  removeFromContext: (id: string) => void;
  clearContext: () => void;
  isInContext: (id: string) => boolean;

  // Text selection (from anywhere on the page)
  textSelection: { text: string; source: 'editor' | 'page'; pageLocation?: string } | null;
  setTextSelection: (sel: { text: string; source: 'editor' | 'page'; pageLocation?: string } | null) => void;

  // Chat state (computed from active tab — backward compatible)
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  clearMessages: () => void;

  // Streaming state (for revise/write modes)
  streamedText: string;
  setStreamedText: React.Dispatch<React.SetStateAction<string>>;
  isStreaming: boolean;
  setIsStreaming: (streaming: boolean) => void;
  generationComplete: boolean;
  setGenerationComplete: (complete: boolean) => void;

  // Programmatic message submission (used by EntryActions to trigger API calls)
  pendingMessage: string | null;
  submitAskMessage: (message: string) => void;
  clearPendingMessage: () => void;

  // Source selection (for Write/Revise modes)
  selectedDocumentIds: string[];
  selectedGuidelineIds: string[];
  selectedImageIds: string[];
  selectedObjectiveIds: string[];
  toggleDocument: (id: string) => void;
  toggleGuideline: (id: string) => void;
  toggleImage: (id: string) => void;
  toggleObjective: (id: string) => void;

  // Open a specific report in the wizard sidebar tab
  pendingReportId: string | null;
  openReport: (reportId: string) => void;
  clearPendingReportId: () => void;

  // Agent / maximize state
  isMaximized: boolean;
  toggleMaximize: () => void;
  /** Exit fullscreen without closing the sidebar panel. */
  exitMaximize: () => void;
  /** Request a specific sidebar panel width (clamped to min/max by UnifiedSidebar). */
  requestSidebarWidth: (width: 'min' | 'max') => void;
  /** Pending width request — consumed and cleared by UnifiedSidebar. */
  pendingSidebarWidth: 'min' | 'max' | null;
  clearPendingSidebarWidth: () => void;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;

  // ─── Multi-tab API ───────────────────────────────────────────────────
  tabs: Map<string, TabState>;
  tabOrder: string[];
  activeTabId: string | null;
  createTab: () => string;
  openConversationTab: (conv: AgentConversationSummary) => void;
  closeTab: (tabId: string) => void;
  switchTab: (tabId: string) => void;
  updateTab: (tabId: string, partial: Partial<TabState>) => void;
}

const STORAGE_KEY = 'ask-sidebar-collapsed';

const DEFAULT_PAGE_CONTEXT: PageContext = {
  pageType: 'other',
  caseId: null,
  reportId: null,
  editor: null,
  editorSelectedText: null,
};

// ─── Context ────────────────────────────────────────────────────────────────────

const AskSidebarContext = createContext<AskSidebarContextType | undefined>(undefined);

// ─── Hook ───────────────────────────────────────────────────────────────────────

export const useAskSidebar = (): AskSidebarContextType => {
  const context = useContext(AskSidebarContext);
  if (context === undefined) {
    throw new Error('useAskSidebar must be used within an AskSidebarProvider');
  }
  return context;
};

// ─── Provider ───────────────────────────────────────────────────────────────────

interface AskSidebarProviderProps {
  children: ReactNode;
  onOpenSidebar?: () => void; // callback to close document viewer panel
}

export const AskSidebarProvider: React.FC<AskSidebarProviderProps> = ({ children, onOpenSidebar }) => {
  const [isOpen, setIsOpen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'false'; // default closed
    } catch {
      return false;
    }
  });
  const [mode, setModeState] = useState<SidebarMode>('ask');
  const [activeSidebarTab, setActiveSidebarTabState] = useState<SidebarTab>('assistant');
  const [pageContext, setPageContextState] = useState<PageContext>(DEFAULT_PAGE_CONTEXT);
  const [contextItems, setContextItems] = useState<ContextItem[]>([]);
  const [textSelection, setTextSelection] = useState<{ text: string; source: 'editor' | 'page'; pageLocation?: string } | null>(null);
  const [streamedText, setStreamedText] = useState('');
  const [isStreamingRevise, setIsStreamingRevise] = useState(false);
  const [generationComplete, setGenerationComplete] = useState(false);

  // Pending message for programmatic submission from EntryActions
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Source selections for write/revise modes
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);
  const [selectedGuidelineIds, setSelectedGuidelineIds] = useState<string[]>([]);
  const [selectedImageIds, setSelectedImageIds] = useState<string[]>([]);
  const [selectedObjectiveIds, setSelectedObjectiveIds] = useState<string[]>([]);

  // Pending report to open in wizard tab
  const [pendingReportId, setPendingReportId] = useState<string | null>(null);

  // Agent / maximize state
  const [isMaximized, setIsMaximized] = useState(false);
  // Sidebar width request — consumed by UnifiedSidebar
  const [pendingSidebarWidth, setPendingSidebarWidth] = useState<'min' | 'max' | null>(null);
  const requestSidebarWidth = useCallback((w: 'min' | 'max') => setPendingSidebarWidth(w), []);
  const clearPendingSidebarWidth = useCallback(() => setPendingSidebarWidth(null), []);

  // ─── Multi-tab state ─────────────────────────────────────────────────
  const [tabs, setTabs] = useState<Map<string, TabState>>(() => {
    const firstId = 'new-1';
    return new Map([[firstId, createEmptyTab(firstId)]]);
  });
  const [tabOrder, setTabOrder] = useState<string[]>(['new-1']);
  const [activeTabId, setActiveTabId] = useState<string | null>('new-1');
  const nextTempIdRef = useRef(2);
  const layoutSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const layoutInitializedRef = useRef(false);

  // Reset all in-memory state on logout to prevent data leaking across accounts
  useEffect(() => {
    const handleLogout = () => {
      setModeState('ask');
      setActiveSidebarTabState('assistant');
      setPageContextState(DEFAULT_PAGE_CONTEXT);
      setContextItems([]);
      setTextSelection(null);
      setStreamedText('');
      setIsStreamingRevise(false);
      setGenerationComplete(false);
      setPendingMessage(null);
      setPendingReportId(null);
      setSelectedDocumentIds([]);
      setSelectedGuidelineIds([]);
      setSelectedImageIds([]);
      setSelectedObjectiveIds([]);
      setIsMaximized(false);
      const freshId = 'new-1';
      setTabs(new Map([[freshId, createEmptyTab(freshId)]]));
      setTabOrder([freshId]);
      setActiveTabId(freshId);
      nextTempIdRef.current = 2;
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // ─── Active tab computed getters ─────────────────────────────────────

  const activeTab = activeTabId ? tabs.get(activeTabId) : undefined;

  const messages = activeTab?.messages ?? [];
  const isLoading = activeTab?.isLoading ?? false;
  const conversationId = activeTab?.conversationId ?? null;

  // ─── Tab-aware setters (target active tab) ───────────────────────────

  const setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>> = useCallback((action) => {
    setTabs(prev => {
      if (!activeTabId) return prev;
      const tab = prev.get(activeTabId);
      if (!tab) return prev;
      const newMessages = typeof action === 'function' ? action(tab.messages) : action;
      const next = new Map(prev);
      next.set(activeTabId, { ...tab, messages: newMessages });
      return next;
    });
  }, [activeTabId]);

  const setIsLoading = useCallback((loading: boolean) => {
    setTabs(prev => {
      if (!activeTabId) return prev;
      const tab = prev.get(activeTabId);
      if (!tab) return prev;
      const next = new Map(prev);
      next.set(activeTabId, { ...tab, isLoading: loading });
      return next;
    });
  }, [activeTabId]);

  const setConversationId = useCallback((id: string | null) => {
    setTabs(prev => {
      if (!activeTabId) return prev;
      const tab = prev.get(activeTabId);
      if (!tab) return prev;
      const next = new Map(prev);
      next.set(activeTabId, { ...tab, conversationId: id });
      return next;
    });
  }, [activeTabId]);

  const clearMessages = useCallback(() => {
    setTabs(prev => {
      if (!activeTabId) return prev;
      const tab = prev.get(activeTabId);
      if (!tab) return prev;
      const next = new Map(prev);
      next.set(activeTabId, { ...tab, messages: [] });
      return next;
    });
  }, [activeTabId]);

  // ─── Generic tab updater (used by streaming callbacks with captured tabId) ──

  const updateTab = useCallback((tabId: string, partial: Partial<TabState>) => {
    setTabs(prev => {
      const tab = prev.get(tabId);
      if (!tab) return prev; // tab closed during stream — no-op
      const next = new Map(prev);
      next.set(tabId, { ...tab, ...partial });
      return next;
    });
  }, []);

  // ─── Tab management ──────────────────────────────────────────────────

  const createTab = useCallback((): string => {
    const newId = `new-${nextTempIdRef.current++}`;
    setTabs(prev => {
      // Evict oldest non-streaming, non-active tab if at limit
      if (prev.size >= MAX_TABS) {
        const candidates = Array.from(prev.values())
          .filter(t => t.tabId !== activeTabId && !t.isStreaming && !t.isLoading)
          .sort((a, b) => a.lastActiveAt - b.lastActiveAt);
        if (candidates.length > 0) {
          const evictId = candidates[0].tabId;
          const next = new Map(prev);
          next.delete(evictId);
          next.set(newId, createEmptyTab(newId));
          setTabOrder(order => [...order.filter(id => id !== evictId), newId]);
          setActiveTabId(newId);
          return next;
        }
      }
      const next = new Map(prev);
      next.set(newId, createEmptyTab(newId));
      return next;
    });
    setTabOrder(prev => [...prev, newId]);
    setActiveTabId(newId);
    return newId;
  }, [activeTabId]);

  const openConversationTab = useCallback((conv: AgentConversationSummary) => {
    // Check if already open
    const existingEntry = Array.from(tabs.entries()).find(([, t]) => t.conversationId === conv.id);
    if (existingEntry) {
      setActiveTabId(existingEntry[0]);
      updateTab(existingEntry[0], { lastActiveAt: Date.now() });
      return;
    }

    const newId = `new-${nextTempIdRef.current++}`;
    const tab: TabState = {
      tabId: newId,
      conversationId: conv.id,
      title: conv.title || 'Untitled',
      messages: [], // will load below
      isLoading: false,
      isStreaming: false,
      lastActiveAt: Date.now(),
    };

    setTabs(prev => {
      if (prev.size >= MAX_TABS) {
        const candidates = Array.from(prev.values())
          .filter(t => t.tabId !== activeTabId && !t.isStreaming && !t.isLoading)
          .sort((a, b) => a.lastActiveAt - b.lastActiveAt);
        if (candidates.length > 0) {
          const evictId = candidates[0].tabId;
          const next = new Map(prev);
          next.delete(evictId);
          next.set(newId, tab);
          setTabOrder(order => [...order.filter(id => id !== evictId), newId]);
          setActiveTabId(newId);
          return next;
        }
      }
      const next = new Map(prev);
      next.set(newId, tab);
      return next;
    });
    setTabOrder(prev => [...prev, newId]);
    setActiveTabId(newId);

    // Load messages from backend
    agentApi.getConversationMessages(conv.id).then(dtos => {
      const loaded: ChatMessage[] = dtos.map(m => parseDtoToMessage(m));
      updateTab(newId, { messages: loaded });
    }).catch(err => {
      console.error('[Tab] Failed to load conversation messages:', err);
    });
  }, [tabs, activeTabId, updateTab]);

  const closeTab = useCallback((tabId: string) => {
    setTabs(prev => {
      const next = new Map(prev);
      next.delete(tabId);
      return next;
    });
    setTabOrder(prev => {
      const newOrder = prev.filter(id => id !== tabId);
      if (activeTabId === tabId) {
        // Switch to adjacent tab
        const oldIdx = prev.indexOf(tabId);
        const newActiveIdx = Math.min(oldIdx, newOrder.length - 1);
        const newActive = newOrder[newActiveIdx] ?? null;
        setActiveTabId(newActive);
        // If no tabs left, create a new one
        if (!newActive) {
          const freshId = `new-${nextTempIdRef.current++}`;
          setTabs(p => new Map(p).set(freshId, createEmptyTab(freshId)));
          setActiveTabId(freshId);
          return [freshId];
        }
      }
      return newOrder;
    });
  }, [activeTabId]);

  const switchTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
    updateTab(tabId, { lastActiveAt: Date.now() });
  }, [updateTab]);

  // ─── Layout persistence (debounced) ──────────────────────────────────

  const caseIdRef = useRef(pageContext.caseId);
  caseIdRef.current = pageContext.caseId;

  const saveLayout = useCallback(() => {
    const caseId = caseIdRef.current;
    if (!caseId) return;

    // Only save tabs with real conversation IDs
    const savedOrder = tabOrder
      .map(id => tabs.get(id))
      .filter((t): t is TabState => !!t && !!t.conversationId)
      .map(t => t.conversationId!);

    const activeConvId = activeTabId ? tabs.get(activeTabId)?.conversationId ?? null : null;

    agentApi.saveTabLayout(caseId, {
      tabOrder: savedOrder,
      activeTabId: activeConvId,
    }).catch(err => {
      console.error('[Tab Layout] Save failed:', err);
    });
  }, [tabOrder, tabs, activeTabId]);

  // Debounce layout saves
  useEffect(() => {
    if (!layoutInitializedRef.current) return; // skip first render
    if (layoutSaveTimerRef.current) clearTimeout(layoutSaveTimerRef.current);
    layoutSaveTimerRef.current = setTimeout(saveLayout, 500);
    return () => {
      if (layoutSaveTimerRef.current) clearTimeout(layoutSaveTimerRef.current);
    };
  }, [tabOrder, activeTabId, saveLayout]);

  // ─── Load layout on case change ──────────────────────────────────────

  useEffect(() => {
    const caseId = pageContext.caseId;
    if (!caseId) {
      layoutInitializedRef.current = false;
      return;
    }

    layoutInitializedRef.current = false; // prevent save during load

    agentApi.getTabLayout(caseId).then(layout => {
      if (layout && layout.tabOrder && layout.tabOrder.length > 0) {
        // Restore tabs from saved layout
        const restoredTabs = new Map<string, TabState>();
        const restoredOrder: string[] = [];

        layout.tabOrder.forEach((convId, idx) => {
          const tabId = `restored-${idx}-${convId.substring(0, 8)}`;
          restoredTabs.set(tabId, {
            tabId,
            conversationId: convId,
            title: 'Loading...',
            messages: [],
            isLoading: false,
            isStreaming: false,
            lastActiveAt: Date.now() - (layout.tabOrder.length - idx),
          });
          restoredOrder.push(tabId);

          // Load messages for each tab
          agentApi.getConversationMessages(convId).then(dtos => {
            const loaded: ChatMessage[] = dtos.map(m => parseDtoToMessage(m));
            setTabs(prev => {
              const tab = prev.get(tabId);
              if (!tab) return prev;
              const next = new Map(prev);
              next.set(tabId, { ...tab, messages: loaded });
              return next;
            });
          }).catch(err => {
            console.error(`[Tab] Failed to load messages for ${convId}:`, err);
          });

          // Load title from conversations list
          agentApi.getConversations(caseId).then(conversations => {
            const conv = conversations.find(c => c.id === convId);
            if (conv) {
              setTabs(prev => {
                const tab = prev.get(tabId);
                if (!tab) return prev;
                const next = new Map(prev);
                next.set(tabId, { ...tab, title: conv.title || 'Untitled' });
                return next;
              });
            }
          }).catch(() => { /* ignore */ });
        });

        // Find active tab
        const activeConvId = layout.activeTabId;
        const activeRestoredTab = activeConvId
          ? restoredOrder.find(id => restoredTabs.get(id)?.conversationId === activeConvId)
          : restoredOrder[0];

        setTabs(restoredTabs);
        setTabOrder(restoredOrder);
        setActiveTabId(activeRestoredTab ?? restoredOrder[0] ?? null);
      } else {
        // No saved layout — start with a single empty tab
        const freshId = `new-${nextTempIdRef.current++}`;
        setTabs(new Map([[freshId, createEmptyTab(freshId)]]));
        setTabOrder([freshId]);
        setActiveTabId(freshId);
      }

      // Allow layout saves after load completes
      setTimeout(() => { layoutInitializedRef.current = true; }, 600);
    }).catch(() => {
      // API error — start fresh
      const freshId = `new-${nextTempIdRef.current++}`;
      setTabs(new Map([[freshId, createEmptyTab(freshId)]]));
      setTabOrder([freshId]);
      setActiveTabId(freshId);
      setTimeout(() => { layoutInitializedRef.current = true; }, 600);
    });
  }, [pageContext.caseId]);

  // ─── Existing callbacks (unchanged) ──────────────────────────────────

  const availableModes = useMemo<SidebarMode[]>(() => {
    switch (pageContext.pageType) {
      case 'report-editor':
        return ['ask', 'revise', 'revise-selection', 'write'];
      case 'case-detail':
      case 'report-viewer':
        return ['ask'];
      default:
        return ['ask'];
    }
  }, [pageContext.pageType]);

  const open = useCallback(() => {
    setIsOpen(true);
    try { localStorage.setItem(STORAGE_KEY, 'false'); } catch { /* noop */ }
    onOpenSidebar?.();
  }, [onOpenSidebar]);

  const close = useCallback(() => {
    setIsOpen(false);
    try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? 'false' : 'true'); } catch { /* noop */ }
      if (next) onOpenSidebar?.();
      return next;
    });
  }, [onOpenSidebar]);

  const setMode = useCallback((newMode: SidebarMode) => {
    setModeState(newMode);
  }, []);

  const setActiveSidebarTab = useCallback((tab: SidebarTab) => {
    setActiveSidebarTabState(tab);
  }, []);

  const setPageContext = useCallback((ctx: Partial<PageContext>) => {
    setPageContextState(prev => {
      const next = { ...prev, ...ctx };
      // When leaving a case/report page, close the sidebar so it doesn't
      // flash the collapsed tab strip on non-case pages.
      if (next.pageType === 'other' && prev.pageType !== 'other') {
        setIsOpen(false);
        setPendingReportId(null);
      }
      // Clear state when caseId changes (tabs reset handled by the useEffect above)
      if (ctx.caseId !== undefined && ctx.caseId !== prev.caseId) {
        setContextItems([]);
        setTextSelection(null);
        setStreamedText('');
        setGenerationComplete(false);
        setIsMaximized(false);
        // Collapse sidebar when switching between cases
        if (prev.caseId !== null) {
          setIsOpen(false);
        }
      }
      // Reset mode if new page doesn't support current mode
      const nextModes = next.pageType === 'report-editor'
        ? ['ask', 'revise', 'revise-selection', 'write']
        : ['ask'];
      setModeState(prev => nextModes.includes(prev) ? prev : 'ask');
      return next;
    });
  }, []);

  const addToContext = useCallback((item: ContextItem) => {
    setContextItems(prev => {
      if (prev.some(i => i.id === item.id)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeFromContext = useCallback((id: string) => {
    setContextItems(prev => prev.filter(i => i.id !== id));
  }, []);

  const clearContext = useCallback(() => {
    setContextItems([]);
  }, []);

  const isInContext = useCallback((id: string) => {
    return contextItems.some(i => i.id === id);
  }, [contextItems]);

  const toggleDocument = useCallback((id: string) => {
    setSelectedDocumentIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }, []);

  const toggleGuideline = useCallback((id: string) => {
    setSelectedGuidelineIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }, []);

  const toggleImage = useCallback((id: string) => {
    setSelectedImageIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }, []);

  const toggleObjective = useCallback((id: string) => {
    setSelectedObjectiveIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  }, []);

  const toggleMaximize = useCallback(() => {
    setIsMaximized(prev => {
      if (!prev) {
        // Maximizing — collapse sidebar
        setIsOpen(false);
        try { localStorage.setItem(STORAGE_KEY, 'true'); } catch { /* noop */ }
      }
      return !prev;
    });
  }, []);

  const exitMaximize = useCallback(() => {
    setIsMaximized(false);
  }, []);

  const submitAskMessage = useCallback((message: string) => {
    setModeState('ask');
    setActiveSidebarTabState('assistant');
    setIsOpen(true);
    try { localStorage.setItem(STORAGE_KEY, 'false'); } catch { /* noop */ }
    onOpenSidebar?.();
    setPendingMessage(message);
  }, [onOpenSidebar]);

  const clearPendingMessage = useCallback(() => {
    setPendingMessage(null);
  }, []);

  const openReport = useCallback((reportId: string) => {
    setActiveSidebarTabState('wizard');
    setIsOpen(true);
    // Don't persist to localStorage — the open state is ephemeral so the
    // sidebar reverts to its previous state when navigating away from the case.
    onOpenSidebar?.();
    setPendingReportId(reportId);
  }, [onOpenSidebar]);

  const clearPendingReportId = useCallback(() => {
    setPendingReportId(null);
  }, []);

  const value = useMemo<AskSidebarContextType>(() => ({
    isOpen,
    open,
    close,
    toggle,
    activeSidebarTab,
    setActiveSidebarTab,
    mode,
    setMode,
    availableModes,
    pageContext,
    setPageContext,
    contextItems,
    addToContext,
    removeFromContext,
    clearContext,
    isInContext,
    textSelection,
    setTextSelection,
    messages,
    setMessages,
    isLoading,
    setIsLoading,
    clearMessages,
    streamedText,
    setStreamedText,
    isStreaming: isStreamingRevise,
    setIsStreaming: setIsStreamingRevise,
    generationComplete,
    setGenerationComplete,
    pendingMessage,
    submitAskMessage,
    clearPendingMessage,
    selectedDocumentIds,
    selectedGuidelineIds,
    selectedImageIds,
    selectedObjectiveIds,
    toggleDocument,
    toggleGuideline,
    toggleImage,
    toggleObjective,
    pendingReportId,
    openReport,
    clearPendingReportId,
    isMaximized,
    toggleMaximize,
    exitMaximize,
    requestSidebarWidth,
    pendingSidebarWidth,
    clearPendingSidebarWidth,
    conversationId,
    setConversationId,
    // Multi-tab
    tabs,
    tabOrder,
    activeTabId,
    createTab,
    openConversationTab,
    closeTab,
    switchTab,
    updateTab,
  }), [
    isOpen, open, close, toggle,
    activeSidebarTab, setActiveSidebarTab,
    mode, setMode, availableModes,
    pageContext, setPageContext,
    contextItems, addToContext, removeFromContext, clearContext, isInContext,
    textSelection,
    messages, setMessages, isLoading, setIsLoading, clearMessages,
    streamedText, isStreamingRevise, generationComplete,
    pendingMessage, submitAskMessage, clearPendingMessage,
    selectedDocumentIds, selectedGuidelineIds, selectedImageIds, selectedObjectiveIds,
    toggleDocument, toggleGuideline, toggleImage, toggleObjective,
    pendingReportId, openReport, clearPendingReportId,
    isMaximized, toggleMaximize, exitMaximize, requestSidebarWidth, pendingSidebarWidth, clearPendingSidebarWidth,
    conversationId, setConversationId,
    tabs, tabOrder, activeTabId,
    createTab, openConversationTab, closeTab, switchTab, updateTab,
  ]);

  return (
    <AskSidebarContext.Provider value={value}>
      {children}
    </AskSidebarContext.Provider>
  );
};
